import { Dialog, DialogContent, DialogTitle, Typography } from '@mui/material';
import { CheckCircle, Clock, Calendar, FileText } from 'lucide-react';
import { useTaskStore, Task } from '../../../stores/TaskStore';
import { useTranslations } from 'next-intl';
import NormalTag from '@/components/ui/Tag';
import CustomMuiScrollBar from '@/components/ui/ScrollBar';
import { useState } from 'react';
import { Plate, usePlateEditor } from 'platejs/react';
import { Editor, EditorContainer } from '@/components/ui/platejs/editor';
import { MarkdownPlugin } from '@platejs/markdown';
import { EditorKit } from '@/components/taskManager/taskDetail/mdEditor/editor-kit';
import Checkbox from '@mui/material/Checkbox';

interface TaskHistoryPanelProps {
  open: boolean;
  onClose: () => void;
}

interface TaskDetailPopupProps {
  open: boolean;
  onClose: () => void;
  task: Task | null;
}

// Markdown 只读渲染组件
const MarkdownViewer = ({ content }: { content: string }) => {
  const editor = usePlateEditor(
    {
      plugins: EditorKit,
      value: (editor) =>
        editor.getApi(MarkdownPlugin).markdown.deserialize(content),
    },
    [content]
  );

  return (
    <Plate editor={editor} readOnly={true}>
      <EditorContainer>
        <Editor
          variant="none"
          className="bg-muted p-3 rounded text-foreground text-sm"
        />
      </EditorContainer>
    </Plate>
  );
};

// 任务详情弹窗组件
const TaskDetailPopup = ({ open, onClose, task }: TaskDetailPopupProps) => {
  const t = useTranslations('TaskHistoryPanel');

  if (!task) return null;

  const createdAtDate = task.createdAt ? new Date(task.createdAt) : null;
  const completedAtDate = task.completedAt ? new Date(task.completedAt) : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{
        '.MuiDialog-paper': {
          borderRadius: '16px',
          overflow: 'hidden',
          backgroundColor: 'var(--color-card)',
          color: 'var(--color-foreground)',
          transition: 'background-color 0.3s ease, color 0.3s ease',
        }
      }}
    >
      <DialogTitle className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <CheckCircle className="h-5 w-5 text-green-500" />
            <Typography className="text-xl">{t('taskDetails')}</Typography>
          </div>
        </div>
      </DialogTitle>

      <DialogContent
        className="p-6"
        sx={{
          maxHeight: '60vh',
          overflowY: 'auto',
          ...CustomMuiScrollBar()
        }}
      >
        <div className="space-y-4">
          {/* 任务标题 */}
          <div>
            <Typography className="text-lg font-semibold text-foreground mb-2">
              {task.title}
            </Typography>
            {task.tags.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {task.tags.map((tag, index) => (
                  <NormalTag key={index} tag={tag} />
                ))}
              </div>
            )}
          </div>

          {/* 时间信息 */}
          <div className="space-y-2 border-t border-border pt-3">
            <div className="flex items-center space-x-2 text-sm">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">{t('createdAt')}:</span>
              <span className="text-foreground">
                {createdAtDate ? createdAtDate.toLocaleString() : '-'}
              </span>
            </div>
            <div className="flex items-center space-x-2 text-sm">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">{t('completedAt')}:</span>
              <span className="text-foreground">
                {completedAtDate ? completedAtDate.toLocaleString() : '-'}
              </span>
            </div>
          </div>

          {/* 子任务列表 */}
          {task.subTasks && task.subTasks.length > 0 && (
            <div className="border-t border-border pt-3">
              <Typography className="text-sm font-medium text-foreground mb-2">
                {t('subTasks')} ({task.subTasks.length})
              </Typography>
              <div className="space-y-1">
                {task.subTasks.map((subTask) => (
                  <div key={subTask.id} className="flex items-start space-x-2 text-sm">
                    <div className={`w-4 h-4 mt-0.5 flex items-center justify-center`}>
                      {subTask.completed ? (
                        <Checkbox disabled defaultChecked />
                      ) : (
                        <Checkbox disabled />
                      )}
                    </div>
                    <span className={'text-foreground'}>
                      {subTask.content}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 备注文档 */}
          {task.comment && (
            <div className="border-t border-border pt-3">
              <div className="flex items-center space-x-2 mb-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <Typography className="text-sm font-medium text-foreground">
                  {t('comment')}
                </Typography>
              </div>
              <MarkdownViewer content={task.comment} />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const TaskHistoryPanel = ({
  open,
  onClose,
}: TaskHistoryPanelProps) => {
  const t = useTranslations('TaskHistoryPanel');
  const completedTasks = useTaskStore(state => state.completedTasks);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const handleTaskClick = (task: Task) => {
    setSelectedTask(task);
    setDetailOpen(true);
  };

  const handleDetailClose = () => {
    setDetailOpen(false);
    setSelectedTask(null);
  };

  const taskData = {
    recentTasks: completedTasks
      .sort((a, b) => {
        // 按完成时间倒序排序（最新的在前）
        const dateA = a.completedAt ? new Date(a.completedAt).getTime() : 0;
        const dateB = b.completedAt ? new Date(b.completedAt).getTime() : 0;
        return dateB - dateA;
      })
      .map(task => {
        const completedAtDate = task.completedAt ? new Date(task.completedAt) : null;
        return {
          task: task,
          title: task.title,
          categories: task.tags,
          completedAt: completedAtDate ? completedAtDate.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : '',
          date: completedAtDate ? completedAtDate.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' }) : '',
        };
      }),
  };

  const renderRecentTasks = () => {
    if (taskData.recentTasks.length === 0) {
      return (
        <div className="text-center py-8 text-muted-foreground">
          <CheckCircle className="h-12 w-12 mx-auto mb-2 text-muted-foreground opacity-50" />
          <div>{t('noCompletedTasks')}</div>
        </div>
      );
    }

    const groupedTasks = taskData.recentTasks.reduce((groups, task) => {
      const date = task.date || new Date(task.completedAt).toLocaleDateString(undefined, {
        month: 'numeric',
        day: 'numeric'
      });
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(task);
      return groups;
    }, {} as Record<string, typeof taskData.recentTasks>);

    return (
      <div className="space-y-4">
        {Object.entries(groupedTasks).map(([date, tasks]) => (
          <div key={date} className="border-b border-border pb-4 last:border-b-0">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <Typography className="text-sm font-medium text-foreground">{date}</Typography>
                <Typography className="text-sm text-muted-foreground">
                  {getDayOfWeek(date)}
                </Typography>
              </div>
              <Typography className="text-sm text-muted-foreground">
                {t('completedTasks', { count: tasks.length })}
              </Typography>
            </div>

            <div className="space-y-2 ml-4">
              {tasks.map((task, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between py-1 cursor-pointer hover:bg-muted/50 rounded px-2 -mx-2 transition-colors"
                  onClick={() => handleTaskClick(task.task)}
                >
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full shadow-sm"></div>
                    <div className="flex items-center space-x-2">
                      <Typography>{task.title}</Typography>
                      {task.categories.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {task.categories.map((category, categoryIndex) => (
                            <NormalTag key={categoryIndex} tag={category} />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <Typography className="text-xs text-muted-foreground">
                    {task.completedAt}
                  </Typography>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const getDayOfWeek = (dateStr: string) => {
    const days = [t('weekdays.sunday'), t('weekdays.monday'), t('weekdays.tuesday'), t('weekdays.wednesday'), t('weekdays.thursday'), t('weekdays.friday'), t('weekdays.saturday')];
    let date = new Date();

    if (dateStr.includes('/')) {
      const [month, day] = dateStr.split('/').map(Number);
      const currentYear = new Date().getFullYear();
      date = new Date(currentYear, month - 1, day);
    } else {
      date = new Date(dateStr);
    }

    return days[date.getDay()];
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        sx={{
          '.MuiDialog-paper': {
            borderRadius: '16px',
            overflow: 'hidden',
            backgroundColor: 'var(--color-card)',
            color: 'var(--color-foreground)',
            transition: 'background-color 0.3s ease, color 0.3s ease',
          }
        }}
      >
        <DialogTitle className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-5 w-5 color-primary" />
              <Typography className="text-xl">{t('taskHistory')}</Typography>
            </div>
            <div className="text-sm text-muted-foreground font-normal">
              {t('recentlyCompleted')}
            </div>
          </div>
        </DialogTitle>

        <DialogContent
          className="p-6"
          sx={{
            maxHeight: '60vh',
            overflowY: 'auto',
            ...CustomMuiScrollBar()
          }}
        >
          <div className="space-y-4">
            {renderRecentTasks()}
          </div>
        </DialogContent>
      </Dialog>

      <TaskDetailPopup
        open={detailOpen}
        onClose={handleDetailClose}
        task={selectedTask}
      />
    </>
  );
};
